import { ElementRef, Injectable, NgZone, inject } from '@angular/core';
import { AbstractControl, FormArray, FormGroup, UntypedFormGroup } from '@angular/forms';
import { FormFieldContainerComponent, type ValidationListItem, type ValidationPopupOptions } from '@tpt/common/form';

import { DynamicFormComponent } from '@app/shared/forms/dynamic-form/dynamic-form/dynamic-form.component';

type FieldAnchor = ElementRef<HTMLElement> | HTMLElement | string;

export interface ReactiveFormValidationListOptions {
  labels?: Record<string, string>;
  anchors?: Record<string, FieldAnchor>;
  root?: ElementRef<HTMLElement> | HTMLElement;
  controlNames?: string[];
  markAllAsTouched?: boolean;
  updateValueAndValidity?: boolean;
}

export interface DynamicFormValidationListOptions {
  markInvalidControlAsTouched?: boolean;
}

export interface FormFieldContainerValidationListOptions {
  validate?: boolean;
}

export interface ValidationListSourceItem {
  name: string;
  title: string;
}

@Injectable({ providedIn: 'root' })
export class DynamicFormValidationListService {
  private readonly ngZone = inject(NgZone);

  public getErrors(
    form: DynamicFormComponent | null | undefined,
    options?: DynamicFormValidationListOptions,
  ): ValidationListItem[];
  public getErrors(
    fieldContainers: Iterable<FormFieldContainerComponent> | null | undefined,
    options?: FormFieldContainerValidationListOptions,
  ): ValidationListItem[];
  public getErrors(
    form: FormGroup | UntypedFormGroup | null | undefined,
    options?: ReactiveFormValidationListOptions,
  ): ValidationListItem[];
  public getErrors(
    source:
      | DynamicFormComponent
      | Iterable<FormFieldContainerComponent>
      | FormGroup
      | UntypedFormGroup
      | null
      | undefined,
    options?:
      | DynamicFormValidationListOptions
      | FormFieldContainerValidationListOptions
      | ReactiveFormValidationListOptions,
  ): ValidationListItem[] {
    if (!source) {
      return [];
    }

    if (this.isDynamicForm(source)) {
      return this.getDynamicFormErrors(source, options as DynamicFormValidationListOptions | undefined);
    }

    if (this.isAngularFormGroup(source)) {
      return this.getReactiveFormErrors(source, options as ReactiveFormValidationListOptions | undefined);
    }

    return this.getFormFieldContainerErrors(
      source as Iterable<FormFieldContainerComponent>,
      options as FormFieldContainerValidationListOptions | undefined,
    );
  }

  public getDynamicFormErrors(
    form: DynamicFormComponent | null | undefined,
    options: DynamicFormValidationListOptions = { markInvalidControlAsTouched: true },
  ): ValidationListItem[] {
    if (!form) {
      return [];
    }

    form.validate(options);

    return (form.validationErrors || []).map((error) => ({
      name: error.name,
      title: error.title,
      navigate: (popupOptions?: ValidationPopupOptions) => this.navigateToField(error.name, popupOptions),
    }));
  }

  public createErrors(errors: readonly ValidationListSourceItem[]): ValidationListItem[] {
    return errors.map((error) => ({
      ...error,
      navigate: (popupOptions?: ValidationPopupOptions) => this.navigateToField(error.name, popupOptions),
    }));
  }

  public getFormFieldContainerErrors(
    fieldContainers: Iterable<FormFieldContainerComponent> | null | undefined,
    options: FormFieldContainerValidationListOptions = { validate: true },
  ): ValidationListItem[] {
    if (!fieldContainers) {
      return [];
    }

    const errors: ValidationListItem[] = [];
    for (const field of fieldContainers) {
      if (options.validate !== false) {
        field.validate();
      }

      if (!field.errors().length) {
        continue;
      }

      const name = field.name() ?? '';
      errors.push({
        name,
        title: field.label() ?? name,
        navigate: (popupOptions?: ValidationPopupOptions) => this.navigateToFormFieldContainer(field, popupOptions),
      });
    }

    return errors;
  }

  public getReactiveFormErrors(
    form: FormGroup | UntypedFormGroup | null | undefined,
    options: ReactiveFormValidationListOptions = {},
  ): ValidationListItem[] {
    if (!form) {
      return [];
    }

    if (options.markAllAsTouched !== false) {
      form.markAllAsTouched();
    }
    if (options.updateValueAndValidity !== false) {
      this.updateControlTreeValidity(form);
    }

    const controlNames = options.controlNames || this.collectInvalidControlNames(form);
    return controlNames
      .filter((name) => form.get(name)?.invalid)
      .map((name) => ({
        name,
        title: options.labels?.[name] || this.humanizeControlName(name),
        navigate: (popupOptions?: ValidationPopupOptions) =>
          this.navigateToReactiveControl(name, options, popupOptions),
      }));
  }

  private navigateToField(name: string, popupOptions?: ValidationPopupOptions): void {
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        const anchor = document.querySelector<HTMLAnchorElement>(`a[name='${name}']`);
        if (!anchor) return;
        anchor.scrollIntoView();
        popupOptions?.closeIfPopupOverlap?.(anchor);
        this.focusField(anchor);
      });
    });
  }

  private navigateToFormFieldContainer(
    field: FormFieldContainerComponent,
    popupOptions?: ValidationPopupOptions,
  ): void {
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        const element = field.el.nativeElement;
        element.scrollIntoView();
        popupOptions?.closeIfPopupOverlap?.(element);
        field.focus();
      });
    });
  }

  private navigateToReactiveControl(
    controlName: string,
    options: ReactiveFormValidationListOptions,
    popupOptions?: ValidationPopupOptions,
  ): void {
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        const element = this.resolveReactiveControlElement(controlName, options);
        if (!element) {
          return;
        }

        element.scrollIntoView();
        popupOptions?.closeIfPopupOverlap?.(element);
        this.focusElement(element);
      });
    });
  }

  private focusField(anchor: HTMLAnchorElement): void {
    try {
      const fieldRoot = anchor.nextElementSibling as HTMLElement | null;
      const invalidElements = fieldRoot?.getElementsByClassName('ng-invalid');
      let focusable = fieldRoot?.querySelector<HTMLElement>('.focusable') || null;

      if (!focusable && invalidElements?.length) {
        const invalidElement = invalidElements[0] as HTMLElement;
        focusable =
          invalidElement.getElementsByTagName('textarea')[0] || invalidElement.getElementsByTagName('input')[0];
      }

      if (!focusable) {
        focusable = fieldRoot?.querySelector<HTMLElement>('.k-widget.ng-invalid') || null;
        focusable?.children[0]?.dispatchEvent(new MouseEvent('click'));
      }

      if (!focusable && !invalidElements?.length) {
        focusable = anchor.parentElement?.querySelector<HTMLElement>('.focusable') || null;
        focusable?.dispatchEvent(new MouseEvent('click'));
      }

      focusable?.focus?.();
    } catch {
      // Keep validation navigation non-blocking: focus is best-effort for heterogeneous dynamic fields.
    }
  }

  private resolveReactiveControlElement(
    controlName: string,
    options: ReactiveFormValidationListOptions,
  ): HTMLElement | null {
    const root = this.unwrapElement(options.root) || document;
    const configuredAnchor = options.anchors?.[controlName];
    const anchor = this.resolveAnchor(configuredAnchor, root);

    if (anchor) {
      return anchor;
    }

    const simpleName = controlName.split('.').at(-1) || controlName;
    return root.querySelector<HTMLElement>(`[formControlName="${simpleName}"]`);
  }

  private resolveAnchor(anchor: FieldAnchor | undefined, root: Document | HTMLElement): HTMLElement | null {
    if (!anchor) {
      return null;
    }

    if (typeof anchor === 'string') {
      return root.querySelector<HTMLElement>(anchor) || root.querySelector<HTMLElement>(`a[name='${anchor}']`);
    }

    return this.unwrapElement(anchor);
  }

  private focusElement(element: HTMLElement): void {
    const focusable = element.matches('input, textarea, select, button, [tabindex], .focusable')
      ? element
      : element.querySelector<HTMLElement>('input, textarea, select, button, [tabindex], .focusable');

    focusable?.focus?.();
  }

  private collectInvalidControlNames(control: AbstractControl, path = ''): string[] {
    if (control instanceof FormGroup) {
      return Object.entries(control.controls).flatMap(([name, child]) =>
        this.collectInvalidControlNames(child, path ? `${path}.${name}` : name),
      );
    }

    if (control instanceof FormArray) {
      return control.controls.flatMap((child, index) =>
        this.collectInvalidControlNames(child, path ? `${path}.${index}` : `${index}`),
      );
    }

    return control.invalid ? [path] : [];
  }

  private updateControlTreeValidity(control: AbstractControl): void {
    if (control instanceof FormGroup || control instanceof FormArray) {
      Object.values(control.controls).forEach((child) => this.updateControlTreeValidity(child));
    }

    control.updateValueAndValidity({ emitEvent: false });
  }

  private humanizeControlName(name: string): string {
    const simpleName = name.split('.').at(-1) || name;
    return simpleName
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .replace(/^\w/, (char) => char.toUpperCase());
  }

  private unwrapElement(element: ElementRef<HTMLElement> | HTMLElement | undefined): HTMLElement | null {
    if (!element) {
      return null;
    }

    return element instanceof ElementRef ? element.nativeElement : element;
  }

  private isDynamicForm(source: unknown): source is DynamicFormComponent {
    return source instanceof DynamicFormComponent;
  }

  private isAngularFormGroup(source: unknown): source is FormGroup | UntypedFormGroup {
    return source instanceof FormGroup;
  }
}
